import { Box } from '@mui/material';
import React from 'react';
import { setAddingDeal } from '../../redux/slices/deal';
import { settingSelectedStage } from '../../redux/slices/stage';
import Stage from './stage_component';
import { StageObject } from '../../params/pipeline';
import { styled } from '@mui/material/styles';
import { AppDispatch, RootState } from '../../redux/store';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';

/**
 * @heading1: RenderStages Component
 * @paragraph: This component renders the stages of a pipeline. It supports drag-and-drop functionality for reordering stages and adding deals to specific stages.
 */
const StepsContainer = styled(Box)({
    display: 'flex',
    // overflowX: 'auto',
    marginTop: '16px',
    paddingBottom: '8px'
});

/**
 * @interface: RenderStagesProps
 * @property: {(result: DropResult) => void | Promise<void>} handleDragEnd - Function called when a drag-and-drop action ends.
 * @property: {StageObject[]} sortedStages - List of stages sorted by their order.
 * @property: {() => void} setAddStage - Callback function to trigger adding a new stage.
 */
interface RenderStagesProps {
    handleDragEnd: (result: DropResult) => void | Promise<void>;
    setAddStage: () => void;
}

/**
 * @heading2: RenderStages
 * @paragraph: Renders a list of stages in a horizontal layout with drag-and-drop support.
 * @param: {RenderStagesProps} props - Props to configure the stages rendering.
 * @returns: {JSXElement} - The rendered stages UI.
 */
const RenderStages: React.FC<RenderStagesProps> = ({ handleDragEnd, setAddStage }: RenderStagesProps) => {
    /**
     * @heading3: Variables
     * @ol:
     * disaptch: {AppDisptach} - The Redux dispatch function to trigger actions.
     */
    const dispatch = useDispatch<AppDispatch>();
    const { selectedPipeline } = useSelector((state: RootState) => state.pipeline);
    const sortedStages = selectedPipeline?.pipeline_stages
        ? [...selectedPipeline.pipeline_stages].sort((a, b) => (a.sort_order as number) - (b.sort_order as number))
        : [];

    const addDealHandler = (stage: StageObject) => {
        dispatch(setAddingDeal(true));
        dispatch(settingSelectedStage(stage.id));
    };

    return (
    <DragDropContext onDragEnd={handleDragEnd}>
        {/* Drag-and-Drop container for stages */}
        <Droppable droppableId="stages" type="stage" direction="horizontal">
            {(provided) => (
                <StepsContainer ref={provided.innerRef} {...provided.droppableProps}>
                    {/* Render each stage */}
                    {sortedStages?.map((stage, index) => (
                        <Stage
                            key={stage.id}
                            stage={stage}
                            index={index}
                            onAddDeal={() => addDealHandler(stage)}
                            onUpdateCall={setAddStage}
                        />
                    ))}
                    {/* Placeholder for drag-and-drop */}
                    {provided.placeholder}
                </StepsContainer>
            )}
        </Droppable>
    </DragDropContext>
    );
};

export default RenderStages;